//-----------------------------------------------------------------------------
//
// footer.js
//
// Copyright 2023 Emergence Software Pte. Ltd. - All rights reserved.
//
//-----------------------------------------------------------------------------

// Imports
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import Cookies from 'js-cookie';
import { Link } from "gatsby"

import { useLocation } from '@reach/router';
import { initializeAndTrack } from '@mobile-reality/gatsby-plugin-gdpr-cookies';

import { menuStructure } from "./menu-structure.js"
import { InternalLink, ExternalLink } from "./header.js"

// Constants
const FOOTER_ID = "sprucely-footer"
const COOKIE_CHANGE_ID = "sprucely-cookie-set"
const SMALL_SCREEN_SIZE = 800;

// Configuration settings
const GOOGLE_ANALYTICS_ID = 'GTM-TTXLBND6'


//-----------------------------------------------------------------------------
//
// SUPPORT FUNCTIONS
//
//-----------------------------------------------------------------------------

// Function allowCookieSetting
export const allowCookieSetting = (location) => {
  // Google tag
  Cookies.set('gatsby-gdpr-google-analytics', true, { expires: 365 })
  Cookies.set('gatsby-gdpr-responded', true, { expires: 365 });
  window[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = false
  initializeAndTrack(location)

  // Microsoft tag
  window.uetq = window.uetq || [];
  window.uetq.push('consent', 'update', { 'ad_storage': 'granted' });
}


// Function denyCookieSetting
export const denyCookieSetting = () => {
  // Google tag
  Cookies.remove('gatsby-gdpr-google-analytics');
  Cookies.set('gatsby-gdpr-responded', true, { expires: 365 });
  window[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = true

  // Microsoft tag
  window.uetq = window.uetq || [];
  window.uetq.push('consent', 'update', { 'ad_storage': 'denied' });
}


//-----------------------------------------------------------------------------
//
// FOOTER
//
//-----------------------------------------------------------------------------

// Function EmergenceFooter
const EmergenceFooter = React.memo(() =>
{
  //-----------------------------------------------------------------------------
  //
  // INITIALIZATION
  //
  //-----------------------------------------------------------------------------

  // States
  const [menuSmall, setMenuSmall] = useState(false);

  // References
  const ref = useRef(undefined);

  // Variables
  const location = useLocation();


  // Effect - Register to screen width changes
  useEffect(() => {
    const changeHeight = () => {
                                 // Determine menu style
                                 setMenuSmall((window.innerWidth <= SMALL_SCREEN_SIZE) ? true : false);
                               }

    changeHeight()
    window.addEventListener('resize', changeHeight)
    return () => { window.removeEventListener('resize', changeHeight) }
  }, [])


  // Startup effect - inject footer under body (else positioning will certinaly fail)
  useEffect(() => {
    if (document && document.body) {
      // Inject footer if it does not exist
      const cookie_option = document.getElementById(COOKIE_CHANGE_ID);
      if (cookie_option) {
        cookie_option.addEventListener('change', changeCookieSetting, false);
      }
    }
  // eslint-disable-next-line
  }, [])


  // Startup effect - set all parent divs to minimum 100 display height for footer to position at bottom correctly
  useLayoutEffect(() => {
    if (ref.current) {
      let parent = ref.current.parentNode
      while (parent) {
        // Identify DIV elements and set the height
        if (parent.tagName && parent.tagName.toUpperCase() === "DIV") parent.style.setProperty('min-height', '100vh');

        // Iterate next parent
        parent = parent.parentNode
      }
    }
  }, [ref])


  //-----------------------------------------------------------------------------
  //
  // SUPPORT FUNCTIONS
  //
  //-----------------------------------------------------------------------------

  // Function readCookieSetting
  const readCookieSetting = () => {
    // Get cookie state
    const cookie_setting = Cookies.get('gatsby-gdpr-google-analytics')
    let allowed = (cookie_setting && (cookie_setting === 'true')) ? true : false

    // Update DOM slider
    const cookie_option = document.getElementById(COOKIE_CHANGE_ID);
    if (cookie_option) cookie_option.checked = allowed

    // Update tracking
    window[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = !allowed
    if (allowed) allowCookieSetting(location)
  }


  // Startup effect - read current cookie setting
  useEffect(() => {
    readCookieSetting()
  // eslint-disable-next-line
  }, [])


  //-----------------------------------------------------------------------------
  //
  // CALLBACKS
  //
  //-----------------------------------------------------------------------------

  // Function changeCookieFromLabelSetting
  const changeCookieFromLabelSetting = useCallback(() => {
    // When the regular form callback is called the slider status
    // is already updated - which we need to do manually if someone
    // clicks on the label
    let cookie_option = document.getElementById(COOKIE_CHANGE_ID);
    cookie_option.checked = !cookie_option.checked
    changeCookieSetting()
  })


  // Function changeCookieSetting
  const changeCookieSetting = useCallback(() => {

    // Get checked state from DOM
    let cookie_option = document.getElementById(COOKIE_CHANGE_ID);
    let currSettingAllowed = cookie_option.checked

    // Enable cookies upon changing from current disallowed state
    if (currSettingAllowed) {
      allowCookieSetting(location)
    }

    // Disable cookies upon changing from current allowed state
    else {
      denyCookieSetting()
      if (window && window.location) window.location.reload();
    }
  }, [location])


  //-----------------------------------------------------------------------------
  //
  // RENDERING - SUBCOMPONENTS
  //
  //-----------------------------------------------------------------------------

  // Function SmallMenuFooter
  const SmallMenuFooter = () =>
  {
    if (!menuSmall) return null
    return <div className="small-menu-bottom">
             {
               menuStructure.map((item, index) => {
                 // Push main menu object to list
                 const DynamicLink = item.url.includes('http') ? ExternalLink : InternalLink

                 return <div key={item.name} style={{ position: 'relative' }}>
                          <DynamicLink url={item.url} className="small_bottom_item">
                              <b className="small_bottom_item">{item.name}</b>
                          </DynamicLink>
                          {((index >= 0) && (index < (menuStructure.length - 1))) && <b className="small_bottom_divider">&nbsp;&nbsp;|&nbsp;&nbsp;</b>}
                        </div>
           })}
           </div>
  }


  //-----------------------------------------------------------------------------
  //
  // RENDERING
  //
  //-----------------------------------------------------------------------------
  return  <>
            <div className="small-menu-space-provider" />
            <footer ref={ref} id={FOOTER_ID} className="footer-bar">
              <SmallMenuFooter />
              <div className="footer-group">
               <div>
                 <Link className="footer-link" to="/terms-service/">Terms of Service</Link>
               </div>
               <div>
                 <Link className="footer-link" to="/privacy-policy/">Privacy Policy</Link>
               </div>
               <div>
                 <Link className="footer-link" to="/acknowledgements/">Acknowledgements</Link>
               </div>
               <div className="footer-highlight">
                 <p className="footer-link" onClick={changeCookieFromLabelSetting}>Accept cookies</p>

                 <div className="form-check form-switch form-switch-center">
                   <input id={COOKIE_CHANGE_ID}
                          className="form-check-input"
                          aria-label="Accept cookies according to Cookie Policy"
                          type="checkbox" />
                 </div>

                 <p className="footer-link">in <Link className="footer-link" to="/cookie-policy/">Cookie Policy</Link></p>
               </div>
             </div>
             <div className="footer-image">
               <div className="footer-link">
                 Powered by&nbsp;
                 <a href="https://www.emergence.com.sg/" target="_blank" rel="noreferrer">
                   <img alt="Emergence Software" height="24px" width="85px" src="/emergence-logo.png" loading="lazy" />
                 </a>
               </div>
             </div>
           </footer>
         </>
})


// Default export
export default EmergenceFooter
